<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import InvoiceCardHeader from '@/components/rc/work-order-dashboard/InvoiceCardHeader.vue'
import { useInvoices } from '@/composables/rc/invoices'
import { type IInvoice } from '@/types/rc/invoices'
import { Icon } from '@iconify/vue'
import { computed } from 'vue'

const props = defineProps<{ invoice: IInvoice; expanded: boolean }>()
const emit = defineEmits(['export', 'delete', 'refresh'])

// Fetch the invoice _details_ when the card is expanded
const { getInvoiceDetailsQuery } = useInvoices()
const invoiceId = computed(() => props.invoice.id)
const queryEnabled = computed(() => props.expanded)
const { data: invoiceDetails, isPending, isError, error } = getInvoiceDetailsQuery(invoiceId, queryEnabled)
</script>

<template>
  <el-card shadow="never">
    <el-collapse-item v-if="invoice" :name="invoice.id">
      <template #title>
        <slot name="header" :invoice-details="invoiceDetails">
          <InvoiceCardHeader
            :invoice="invoice"
            :expanded="expanded"
            @export="emit('export', invoice.id)"
            @delete="emit('delete', invoice.id)"
            @refresh="emit('refresh', invoice.id)"
          >
            <!-- RWO -->
            <h4 class="whitespace text-sm w-52">
              {{ invoice.work_order_number }}
            </h4>

            <!-- Customer -->
            <h4 class="whitespace text-sm w-72">
              <span class="text-text-tertiary">Customer</span>
              {{ invoice.customer.name }}
            </h4>

            <!-- Invoice total -->
            <h4 class="whitespace-nowrap text-sm w-24">
              <span class="text-text-tertiary">Total:</span> ${{ invoice.total }}
            </h4>
          </InvoiceCardHeader>
        </slot>
      </template>

      <RlErrorAlert v-if="isError" :error="error" />
      <div v-if="isPending" class="w-full h-32 flex items-center justify-center">
        <Icon icon="eos-icons:three-dots-loading" height="20" />
      </div>
      <slot v-if="!isPending" name="body" :invoice-details="invoiceDetails" />
    </el-collapse-item>
  </el-card>
</template>

<style scoped lang="scss">
/* Override Element Plus styles */
:deep() {
  .el-collapse-item__header {
    border: none;
    display: flex;
    flex-direction: row-reverse;
    height: 32px;
  }

  .el-collapse-item__wrap {
    border-bottom: 0;
  }

  .el-table tr {
    background: transparent;
  }

  .el-table__row {
    border-bottom: 0;
    cursor: pointer;
  }
}
</style>
